import { useScopedAtomValue, useSetScopedAtom } from '@lemon8/web-app-shared/ssr';
import { FC, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import SimpleVideo, { SimpleVideoForwardProps, VideoStatus } from '~/components/simple-video/index';
import PlayFill from 'icon/play_fill.svg';
import SpeakerMutedFillIcon from 'icon/speaker_x_mark_fill.svg';
import SpeakerWaveFillIcon from 'icon/speaker_wave_2_fill.svg';
import { useAtomValue, useSetAtom } from 'jotai';
import { $SimpleCTAModalShow } from '~/models/portal/simple-cta-modal';
import { ClickPosition } from '~/utils/click-position';
import { useTranslation } from 'react-i18next';
import { $IsMobileUser } from '~/utils/is-mobile-user';
import { setLocalStorageStatus } from 'shared/utils/local-storage-util';
import { useActiveToArticle } from '~/utils/active/use-active-to-article';
import { PostDetailVideoPlayer } from './atom';
import { PostDetailModalAtoms } from '../../atoms';
import { VideoEndRecommend } from '~/components/video-end-recommend';
import { $CurrentPositionPrefix } from '~/models/current-position-prefix';

import './style.scss';

export const VideoPlayer: FC = () => {
  const { t } = useTranslation();
  const isMobileUser = useAtomValue($IsMobileUser);
  const player = useRef<SimpleVideoForwardProps>(null);
  const article = useScopedAtomValue(PostDetailModalAtoms.$CurrentVideoArticle);
  const { video, largeImage } = article;
  const setEndRecommendShow = useSetScopedAtom(PostDetailVideoPlayer.$EndRecommendShow);
  const prefix = useScopedAtomValue($CurrentPositionPrefix);
  const isFirstPlay = useRef(true);
  const setSimpleCTAModal = useSetAtom($SimpleCTAModalShow);
  const [showPlay, setShowPlay] = useState(false);
  const [localStorageKey, setLocalStorageKey] = useState('');
  const [muted, setMuted] = useState(true);
  const keyPrefix = 'seo_video_first_visit';
  const videoClicked = useRef(false);
  const setVideoReplayHandler = useSetScopedAtom(PostDetailVideoPlayer.$ReplayVideo);
  const activeToArticle = useActiveToArticle(article);

  const show = useScopedAtomValue(PostDetailVideoPlayer.$EndRecommendShow);
  const relatedPost = useScopedAtomValue(PostDetailModalAtoms.$CurrentArticleRelatedPost);
  const { handler } = useScopedAtomValue(PostDetailVideoPlayer.$ReplayVideo);

  useEffect(() => {
    const onVisibilitychange = () => {
      if (document.visibilityState === 'hidden') {
        window.Tea('wap_click', {
          current_position: prefix + ClickPosition.main_video_pause_auto,
        });
        player.current?.pause();
        setShowPlay(true);
      } else {
        if (player.current?.status === VideoStatus.PLAYING && showPlay) {
          setShowPlay(false);
        }
      }
    };

    document.addEventListener('visibilitychange', onVisibilitychange);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilitychange);
    };
  }, []);

  useEffect(() => {
    if (!player.current) {
      return;
    }

    const handlePlayEvent = () => {
      if (isFirstPlay.current) {
        console.log(isFirstPlay.current, 'isFirstPlay');
        window.Tea('wap_video_play');
      }
      isFirstPlay.current = false;
      setShowPlay(false);
      setEndRecommendShow(false);
    };

    const handlePlayingEvent = () => {
      setShowPlay(false);
      setEndRecommendShow(false);
    };

    const handlePauseEvent = () => {
      setShowPlay(true);
    };

    const handleEndEvent = () => {
      setEndRecommendShow(true);
    };

    const handleVolumeChange = () => {
      const video = player.current?.video;
      if (!video) {
        return;
      }

      setMuted(video.muted);
    };

    const playerInst = player.current;

    playerInst.addEventListener('play', handlePlayEvent);
    playerInst.addEventListener('playing', handlePlayingEvent);
    playerInst.addEventListener('pause', handlePauseEvent);
    playerInst.addEventListener('ended', handleEndEvent);
    playerInst.addEventListener('volumechange', handleVolumeChange);

    return () => {
      playerInst.removeEventListener('play', handlePlayEvent);
      playerInst.removeEventListener('playing', handlePlayingEvent);
      playerInst.removeEventListener('pause', handlePauseEvent);
      playerInst.removeEventListener('ended', handleEndEvent);
      playerInst.removeEventListener('volumechange', handleVolumeChange);
    };
  }, []);

  useEffect(() => {
    const dateToday = ((): string => {
      const today = new Date();
      const date = `${today.getFullYear()}-${today.getMonth() + 1}-${today.getDate()}`;
      return date;
    })();
    const key = `${keyPrefix}_${dateToday}`;
    // first visit or clicks open last time, use manual play mode
    const lastVideoVisitState = window.localStorage.getItem(key) || '0';
    // '0' means first landing, '1' means user clicks cancel, '2' means click open last time
    const useManualPlay = isMobileUser && (lastVideoVisitState === '0' || lastVideoVisitState === '2');
    if (useManualPlay) {
      setShowPlay(useManualPlay);
    } else {
      player.current?.play();
    }
    setLocalStorageKey(key);
  }, []);

  const handleVideoClick = useCallback(() => {
    window.Tea('wap_click', {
      current_position: prefix + ClickPosition.main_video_pause_manual,
    });
    player.current?.pause();

    if (!videoClicked.current && isMobileUser) {
      setSimpleCTAModal({
        body: t('seo_stopVideo_modal_desc'),
        currentPosition: [prefix, ClickPosition.main_video_pause_manual_modal],
        handleClickConfirm: () => {
          activeToArticle([prefix, ClickPosition.main_video_pause_manual_modal]);
        },
      });

      videoClicked.current = true;
    }
  }, []);

  const handlePlayClick = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      window.Tea('wap_click', {
        current_position: prefix + ClickPosition.main_video_play,
      });

      const lastVideoVisitState = window.localStorage.getItem(localStorageKey) || '0';
      const useManualPlay = isMobileUser && (lastVideoVisitState === '0' || lastVideoVisitState === '2');

      if (useManualPlay) {
        setSimpleCTAModal({
          body: t('seo_stopVideo_modal_desc'),
          currentPosition: [prefix, ClickPosition.main_video_play_modal],
          handleClickConfirm: () => {
            setLocalStorageStatus(keyPrefix, localStorageKey, '2');
            activeToArticle([prefix, ClickPosition.main_video_play_modal]);
          },
          handleClickCancel() {
            setLocalStorageStatus(keyPrefix, localStorageKey, '1');
            player.current?.play();
          },
        });
      } else {
        player.current?.play();
      }
    },
    [localStorageKey, activeToArticle],
  );

  const handleVolumeClicked = useCallback(() => {
    if (player.current) {
      if (player.current.muted) {
        player.current.unmute();
      } else {
        player.current.mute();
      }
    }
  }, []);

  const videoReplayHandler = useCallback(() => {
    if (player.current) {
      player.current.seek(0);
      player.current.play();
    }
  }, [player]);

  useEffect(() => {
    setVideoReplayHandler({ handler: videoReplayHandler });
  }, [setVideoReplayHandler, videoReplayHandler]);

  return (
    <div className="post-detail-modal-article-video">
      <SimpleVideo
        ref={player}
        className="article-video-player"
        videoSrc={video.encryptedUrl || video.url}
        posterSrc={largeImage.url}
        onClick={handleVideoClick}
        muteStatus={video.muteStatus}
      />
      {showPlay && (
        <div className="article-video-play-icon" onClick={handlePlayClick}>
          <PlayFill />
        </div>
      )}
      <div className="article-video-volume-icon" onClick={handleVolumeClicked}>
        {muted ? <SpeakerMutedFillIcon /> : <SpeakerWaveFillIcon />}
      </div>
      <VideoEndRecommend
        positionPrefix={prefix}
        article={article}
        show={show}
        relatedPost={relatedPost}
        onClickReplay={() => handler()}
      />
      ;
    </div>
  );
};
